import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ColorTab } from "@/components/ui/color-tab";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { inr, fmtDate, statusColor, initials } from "@/lib/utils-format";
import { Plus, Trash2, CheckCircle2, XCircle, IndianRupee, CalendarCheck, Users, Clock, PlaneTakeoff, LayoutDashboard, ArrowRightLeft, Timer, Factory, Shirt, BadgeIndianRupee, FileWarning, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import {
  HRDashboardPanel, ShiftsPanel, TransfersPanel, OvertimePanel,
  ProductionLinesPanel, UniformsPanel, LoansPanel, DocumentsPanel,
  ReportsPanel, ResignButton,
} from "@/pages/HRPanels";

const emptyEmp = { name: "", employee_code: "", email: "", phone: "", department: "production", designation: "", salary: 0, join_date: "", address: "", active: true, work_status: "active", primary_branch_id: "", allocations: [], default_shift_id: "", production_line: "", ot_hourly_rate: "" };
const emptyAtt = { employee_id: "", date: new Date().toISOString().slice(0, 10), check_in: "", check_out: "", status: "present", notes: "", shift_id: "", branch_id: "" };
const emptyLeave = { employee_id: "", leave_type: "casual", from_date: "", to_date: "", reason: "" };
const emptySal = { employee_id: "", month: new Date().toISOString().slice(0, 7), base_salary: 0, bonuses: 0, deductions: 0, method: "bank_transfer", notes: "", auto_include_ot: true, auto_deduct_loans: true };

const DEPARTMENTS = ["production", "packaging", "sales", "delivery", "admin", "kitchen", "hr", "accounts", "quality", "maintenance"];
const WORK_STATUSES = ["active", "on_leave", "probation", "notice_period", "suspended", "resigned"];
const WORK_STATUS_TONE = {
  active: "bg-emerald-100 text-emerald-800 border-emerald-200",
  on_leave: "bg-amber-100 text-amber-800 border-amber-200",
  probation: "bg-blue-100 text-blue-800 border-blue-200",
  notice_period: "bg-purple-100 text-purple-800 border-purple-200",
  suspended: "bg-red-100 text-red-800 border-red-200",
  resigned: "bg-stone-100 text-stone-600 border-stone-200",
};
const LEAVE_TYPES = ["casual", "sick", "paid", "unpaid"];
const ATT_STATUSES = ["present", "absent", "half_day", "leave"];

export default function HRPage() {
  const [tab, setTab] = useState("dashboard");
  const [employees, setEmployees] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [salaries, setSalaries] = useState([]);
  const [branches, setBranches] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [empOpen, setEmpOpen] = useState(false);
  const [attOpen, setAttOpen] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [salOpen, setSalOpen] = useState(false);
  const [empForm, setEmpForm] = useState(emptyEmp);
  const [attForm, setAttForm] = useState(emptyAtt);
  const [leaveForm, setLeaveForm] = useState(emptyLeave);
  const [salForm, setSalForm] = useState(emptySal);
  const [editingEmp, setEditingEmp] = useState(null);
  const [editingSal, setEditingSal] = useState(null);
  const [salPreview, setSalPreview] = useState(null);
  const [empSearch, setEmpSearch] = useState("");

  const load = async () => {
    const [e, a, l, br, sh] = await Promise.all([
      api.get("/hr/employees"), api.get("/hr/attendance"), api.get("/hr/leaves"),
      api.get("/branches"), api.get("/hr/shifts"),
    ]);
    setEmployees(e.data); setAttendance(a.data); setLeaves(l.data);
    setBranches((br.data || []).filter((b) => b.is_active !== false));
    setShifts(sh.data);
    try { const s = await api.get("/hr/salary"); setSalaries(s.data); } catch (_) { setSalaries([]); }
  };
  useEffect(() => { load(); }, []);

  // Auto-load salary preview (OT + loan EMI) when employee + month is picked
  useEffect(() => {
    if (!salForm.employee_id || !salForm.month || editingSal) { setSalPreview(null); return; }
    api.get("/hr/salary/preview", { params: { employee_id: salForm.employee_id, month: salForm.month } })
      .then((r) => setSalPreview(r.data)).catch(() => setSalPreview(null));
  }, [salForm.employee_id, salForm.month, editingSal]);

  const branchName = (id) => branches.find((b) => b.id === id)?.name || "—";
  const branchType = (id) => branches.find((b) => b.id === id)?.type;

  const filteredEmployees = React.useMemo(() => {
    const q = empSearch.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter((e) => {
      const hay = [
        e.name, e.employee_code, e.email, e.phone, e.department, e.designation,
        e.production_line, branchName(e.primary_branch_id), (e.work_status || "").replace("_", " "),
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [employees, empSearch, branches]);

  const submitEmp = async () => {
    if (!empForm.name || !empForm.employee_code) return toast.error("Name & code required");
    try {
      const payload = { ...empForm, salary: Number(empForm.salary) || 0 };
      if (payload.ot_hourly_rate === "" || payload.ot_hourly_rate == null) delete payload.ot_hourly_rate;
      else payload.ot_hourly_rate = Number(payload.ot_hourly_rate);
      if (editingEmp) await api.patch(`/hr/employees/${editingEmp}`, payload);
      else await api.post("/hr/employees", payload);
      toast.success("Saved");
      setEmpOpen(false); setEmpForm(emptyEmp); setEditingEmp(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const submitAtt = async () => {
    if (!attForm.employee_id || !attForm.date) return toast.error("Employee & date required");
    try { await api.post("/hr/attendance", attForm); toast.success("Attendance marked"); setAttOpen(false); setAttForm(emptyAtt); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const submitLeave = async () => {
    if (!leaveForm.employee_id || !leaveForm.from_date || !leaveForm.to_date) return toast.error("Employee & dates required");
    try { await api.post("/hr/leaves", leaveForm); toast.success("Leave requested"); setLeaveOpen(false); setLeaveForm(emptyLeave); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const submitSal = async () => {
    if (!salForm.employee_id || !salForm.month) return toast.error("Employee & month required");
    try {
      const payload = { ...salForm, base_salary: Number(salForm.base_salary), bonuses: Number(salForm.bonuses), deductions: Number(salForm.deductions) };
      if (editingSal) {
        await api.patch(`/hr/salary/${editingSal}`, { base_salary: payload.base_salary, bonuses: payload.bonuses, deductions: payload.deductions, notes: payload.notes, method: payload.method });
        toast.success("Salary updated");
      } else {
        await api.post("/hr/salary", payload);
        toast.success("Salary recorded" + (payload.auto_include_ot || payload.auto_deduct_loans ? " (auto-flow applied)" : ""));
      }
      setSalOpen(false); setSalForm(emptySal); setEditingSal(null); setSalPreview(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const decideLeave = async (l, status) => {
    await api.patch(`/hr/leaves/${l.id}`, { status });
    toast.success(status === "approved" ? "Approved" : "Rejected");
    load();
  };

  const markPaid = async (s) => {
    await api.post(`/hr/salary/${s.id}/mark-paid`);
    toast.success("Marked paid");
    load();
  };

  return (
    <div className="space-y-6" data-testid="hr-page">
      <div>
        <div className="uc-label">People</div>
        <h1 className="font-[Outfit] text-3xl sm:text-4xl font-semibold tracking-tight mt-1">HR & Employees</h1>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap h-auto bg-transparent p-0 gap-0">
          <ColorTab value="dashboard" tone="orange" icon={LayoutDashboard} testid="hr-tab-dashboard">Dashboard</ColorTab>
          <ColorTab value="employees" tone="blue" icon={Users} testid="hr-tab-employees">Employees ({employees.length})</ColorTab>
          <ColorTab value="attendance" tone="orange" icon={CalendarCheck} testid="hr-tab-attendance">Attendance</ColorTab>
          <ColorTab value="shifts" tone="blue" icon={Clock} testid="hr-tab-shifts">Shifts</ColorTab>
          <ColorTab value="leaves" tone="amber" icon={PlaneTakeoff} testid="hr-tab-leaves" badge={leaves.filter((l) => l.status === "pending").length}>Leaves</ColorTab>
          <ColorTab value="overtime" tone="amber" icon={Timer} testid="hr-tab-overtime">Overtime</ColorTab>
          <ColorTab value="lines" tone="orange" icon={Factory} testid="hr-tab-lines">Prod. Lines</ColorTab>
          <ColorTab value="transfers" tone="purple" icon={ArrowRightLeft} testid="hr-tab-transfers">Transfers</ColorTab>
          <ColorTab value="uniforms" tone="slate" icon={Shirt} testid="hr-tab-uniforms">Uniforms</ColorTab>
          <ColorTab value="loans" tone="red" icon={BadgeIndianRupee} testid="hr-tab-loans">Loans</ColorTab>
          <ColorTab value="documents" tone="red" icon={FileWarning} testid="hr-tab-documents">Documents</ColorTab>
          <ColorTab value="salary" tone="purple" icon={IndianRupee} testid="hr-tab-salary">Salary</ColorTab>
          <ColorTab value="reports" tone="green" icon={BarChart3} testid="hr-tab-reports">Reports</ColorTab>
        </TabsList>

        {/* Dashboard */}
        <TabsContent value="dashboard"><HRDashboardPanel shifts={shifts} branches={branches} /></TabsContent>

        {/* Shifts */}
        <TabsContent value="shifts"><ShiftsPanel branches={branches} /></TabsContent>

        {/* Overtime */}
        <TabsContent value="overtime"><OvertimePanel employees={employees} /></TabsContent>

        {/* Production Lines */}
        <TabsContent value="lines"><ProductionLinesPanel employees={employees} branches={branches} reload={load} /></TabsContent>

        {/* Transfers */}
        <TabsContent value="transfers"><TransfersPanel employees={employees} branches={branches} /></TabsContent>

        {/* Uniforms & ID */}
        <TabsContent value="uniforms"><UniformsPanel employees={employees} /></TabsContent>

        {/* Loans */}
        <TabsContent value="loans"><LoansPanel employees={employees} /></TabsContent>

        {/* Documents */}
        <TabsContent value="documents"><DocumentsPanel employees={employees} /></TabsContent>

        {/* Reports */}
        <TabsContent value="reports"><ReportsPanel employees={employees} /></TabsContent>

        {/* Employees */}
        <TabsContent value="employees" className="pt-4">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <div className="relative flex-1 max-w-md">
              <Input
                data-testid="hr-emp-search"
                value={empSearch}
                onChange={(e) => setEmpSearch(e.target.value)}
                placeholder="Search staff — name, code, phone, department, branch, line…"
                className="pl-9"
              />
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8B7355]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8" strokeWidth="2"/><path d="m21 21-4.35-4.35" strokeWidth="2" strokeLinecap="round"/></svg>
              {empSearch && (
                <div className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-[#8B7355]" data-testid="hr-emp-search-count">
                  {filteredEmployees.length} / {employees.length}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <AssignBranchButton employees={employees} branches={branches} onDone={load} />
              <Dialog open={empOpen} onOpenChange={(o) => { setEmpOpen(o); if (!o) { setEmpForm(emptyEmp); setEditingEmp(null); } }}>
                <DialogTrigger asChild><Button data-testid="hr-emp-add-btn" className="bg-[#C05621] hover:bg-[#9C4221] text-white"><Plus className="w-4 h-4 mr-1" /> Recruit Staff</Button></DialogTrigger>
              <DialogContent className="max-w-2xl bg-white">
                <DialogHeader><DialogTitle className="font-[Outfit]">{editingEmp ? "Edit" : "New"} Employee</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3 py-2">
                  <div><Label>Name *</Label><Input data-testid="hr-emp-name-input" value={empForm.name} onChange={(e) => setEmpForm({ ...empForm, name: e.target.value })} /></div>
                  <div><Label>Code *</Label><Input data-testid="hr-emp-code-input" value={empForm.employee_code} onChange={(e) => setEmpForm({ ...empForm, employee_code: e.target.value })} /></div>
                  <div><Label>Email</Label><Input value={empForm.email || ""} onChange={(e) => setEmpForm({ ...empForm, email: e.target.value })} /></div>
                  <div><Label>Phone</Label><Input value={empForm.phone || ""} onChange={(e) => setEmpForm({ ...empForm, phone: e.target.value })} /></div>
                  <div>
                    <Label>Department</Label>
                    <Select value={empForm.department} onValueChange={(v) => setEmpForm({ ...empForm, department: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">{DEPARTMENTS.map((d) => <SelectItem key={d} value={d} className="capitalize">{d}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Designation</Label><Input value={empForm.designation || ""} onChange={(e) => setEmpForm({ ...empForm, designation: e.target.value })} /></div>
                  <div><Label>Salary (₹ / month)</Label><Input type="number" value={empForm.salary} onChange={(e) => setEmpForm({ ...empForm, salary: e.target.value })} /></div>
                  <div><Label>Join date</Label><Input type="date" value={empForm.join_date || ""} onChange={(e) => setEmpForm({ ...empForm, join_date: e.target.value })} /></div>

                  {/* Multi-site: primary branch + allocations */}
                  <div className="col-span-2 border-t border-[#EAE5D9] pt-3 mt-1">
                    <div className="uc-label mb-2">Workplace</div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Primary Branch *</Label>
                        <Select value={empForm.primary_branch_id || "__none__"} onValueChange={(v) => setEmpForm({ ...empForm, primary_branch_id: v === "__none__" ? "" : v })}>
                          <SelectTrigger data-testid="hr-emp-primary-branch"><SelectValue placeholder="Head Office / Factory / Showroom" /></SelectTrigger>
                          <SelectContent className="bg-white max-h-72">
                            <SelectItem value="__none__">— none —</SelectItem>
                            {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name} · {b.type}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Default Shift</Label>
                        <Select value={empForm.default_shift_id || "__none__"} onValueChange={(v) => setEmpForm({ ...empForm, default_shift_id: v === "__none__" ? "" : v })}>
                          <SelectTrigger data-testid="hr-emp-default-shift"><SelectValue placeholder="— none —" /></SelectTrigger>
                          <SelectContent className="bg-white max-h-72">
                            <SelectItem value="__none__">— none —</SelectItem>
                            {shifts.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} ({s.start_time}–{s.end_time})</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div><Label>Production Line</Label><Input value={empForm.production_line || ""} onChange={(e) => setEmpForm({ ...empForm, production_line: e.target.value })} placeholder="e.g. Line A · Cake" /></div>
                      <div>
                        <Label>Work Status</Label>
                        <Select value={empForm.work_status || "active"} onValueChange={(v) => setEmpForm({ ...empForm, work_status: v })}>
                          <SelectTrigger data-testid="hr-emp-work-status"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-white">
                            {WORK_STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace("_", " ")}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <Label>OT Hourly Rate Override (₹/h) — optional</Label>
                        <Input type="number" step="0.01" value={empForm.ot_hourly_rate ?? ""} onChange={(e) => setEmpForm({ ...empForm, ot_hourly_rate: e.target.value === "" ? "" : Number(e.target.value) })} placeholder="Leaves blank = use dept default or salary÷240" data-testid="hr-emp-ot-rate" />
                      </div>
                      <div className="col-span-2">
                        <Label>Secondary Allocations</Label>
                        <div className="border border-[#EAE5D9] rounded-md p-2 max-h-40 overflow-y-auto space-y-1" data-testid="hr-emp-allocations">
                          {branches.filter((b) => b.id !== empForm.primary_branch_id).map((b) => {
                            const checked = (empForm.allocations || []).some((a) => a.branch_id === b.id);
                            return (
                              <label key={b.id} className="flex items-center gap-2 text-sm hover:bg-[#FAF6EE] px-2 py-1 rounded cursor-pointer">
                                <input type="checkbox" className="w-4 h-4" checked={checked}
                                  onChange={(e) => {
                                    const list = (empForm.allocations || []).filter((a) => a.branch_id !== b.id);
                                    if (e.target.checked) list.push({ branch_id: b.id, from_date: new Date().toISOString().slice(0, 10), active: true });
                                    setEmpForm({ ...empForm, allocations: list });
                                  }} />
                                <span>{b.name} <span className="text-xs text-[#8B7355]">· {b.type}</span></span>
                              </label>
                            );
                          })}
                          {branches.length <= 1 && <div className="text-xs text-[#8B7355] italic px-2 py-1">Only one branch — no secondary allocations possible.</div>}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-span-2"><Label>Address</Label><Textarea rows={2} value={empForm.address || ""} onChange={(e) => setEmpForm({ ...empForm, address: e.target.value })} /></div>
                  <div className="col-span-2 flex items-center gap-2"><Switch checked={empForm.active} onCheckedChange={(v) => setEmpForm({ ...empForm, active: v })} /><Label>Active</Label></div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setEmpOpen(false)}>Cancel</Button>
                  <Button onClick={submitEmp} data-testid="hr-emp-save-btn" className="bg-[#C05621] hover:bg-[#9C4221] text-white">Save</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            </div>
          </div>
          <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left uc-label border-b border-[#EAE5D9]"><th className="px-4 py-3">Name</th><th className="px-4 py-3">Code</th><th className="px-4 py-3">Department</th><th className="px-4 py-3">Designation</th><th className="px-4 py-3">Primary Branch</th><th className="px-4 py-3">Line</th><th className="px-4 py-3">Phone</th><th className="px-4 py-3 text-right">Salary</th><th className="px-4 py-3">Joined</th><th className="px-4 py-3">Status</th><th></th></tr></thead>
              <tbody>
                {filteredEmployees.map((e) => (
                  <tr key={e.id} className="border-b border-[#EAE5D9] last:border-0 hover:bg-[#FDFBF7]" data-testid={`hr-emp-row-${e.id}`}>
                    <td className="px-4 py-3 font-medium flex items-center gap-2 cursor-pointer" onClick={() => { setEmpForm({ ...emptyEmp, ...e, allocations: e.allocations || [] }); setEditingEmp(e.id); setEmpOpen(true); }}>
                      <div className="w-8 h-8 rounded-full bg-[#C05621] text-white flex items-center justify-center text-xs font-semibold">{initials(e.name)}</div>
                      {e.name}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{e.employee_code}</td>
                    <td className="px-4 py-3"><Badge variant="outline" className="capitalize">{e.department}</Badge></td>
                    <td className="px-4 py-3">{e.designation || "—"}</td>
                    <td className="px-4 py-3 text-xs">
                      {e.primary_branch_id ? (
                        <span><span className="font-medium">{branchName(e.primary_branch_id)}</span> <span className="text-[#8B7355] capitalize">· {branchType(e.primary_branch_id)?.replace("_", " ")}</span></span>
                      ) : <span className="text-[#8B7355] italic">unassigned</span>}
                      {(e.allocations || []).length > 0 && <Badge className="ml-1 bg-purple-100 text-purple-800 border-0 text-[10px]">+{(e.allocations || []).length}</Badge>}
                    </td>
                    <td className="px-4 py-3 text-xs">{e.production_line || <span className="text-[#8B7355]">—</span>}</td>
                    <td className="px-4 py-3">{e.phone || "—"}</td>
                    <td className="px-4 py-3 text-right font-semibold">{inr(e.salary)}</td>
                    <td className="px-4 py-3 text-[#8B7355]">{fmtDate(e.join_date)}</td>
                    <td className="px-4 py-3">
                      {e.active ? (
                        <Badge variant="outline" className={`${WORK_STATUS_TONE[e.work_status || "active"]} capitalize`}>{(e.work_status || "active").replace("_", " ")}</Badge>
                      ) : <Badge variant="outline" className="bg-stone-100 text-stone-600">Inactive</Badge>}
                    </td>
                    <td className="px-4 py-3 flex items-center gap-2">
                      {e.active && <ResignButton employee={e} onDone={load} />}
                      <button onClick={async () => { if(window.confirm(`Delete ${e.name}?`)) { await api.delete(`/hr/employees/${e.id}`); load(); }}} className="text-red-500"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
                {employees.length === 0 && <tr><td colSpan={11} className="text-center py-8 text-[#8B7355]">No employees yet</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Attendance */}
        <TabsContent value="attendance" className="pt-4">
          <div className="flex justify-end mb-3">
            <Dialog open={attOpen} onOpenChange={setAttOpen}>
              <DialogTrigger asChild><Button data-testid="hr-att-add-btn" className="bg-[#C05621] hover:bg-[#9C4221] text-white"><CalendarCheck className="w-4 h-4 mr-1" /> Mark Attendance</Button></DialogTrigger>
              <DialogContent className="max-w-md bg-white">
                <DialogHeader><DialogTitle className="font-[Outfit]">Mark Attendance</DialogTitle></DialogHeader>
                <div className="space-y-3 py-2">
                  <div>
                    <Label>Employee *</Label>
                    <Select value={attForm.employee_id} onValueChange={(v) => { const emp = employees.find((x) => x.id === v); setAttForm({ ...attForm, employee_id: v, shift_id: emp?.default_shift_id || "", branch_id: emp?.primary_branch_id || "" }); }}>
                      <SelectTrigger data-testid="hr-att-emp-select"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent className="bg-white max-h-72">{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} · {e.employee_code}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Date</Label><Input type="date" value={attForm.date} onChange={(e) => setAttForm({ ...attForm, date: e.target.value })} /></div>
                    <div>
                      <Label>Status</Label>
                      <Select value={attForm.status} onValueChange={(v) => setAttForm({ ...attForm, status: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white">{ATT_STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Shift</Label>
                      <Select value={attForm.shift_id || "__none__"} onValueChange={(v) => setAttForm({ ...attForm, shift_id: v === "__none__" ? "" : v })}>
                        <SelectTrigger data-testid="hr-att-shift"><SelectValue placeholder="— none —" /></SelectTrigger>
                        <SelectContent className="bg-white max-h-72">
                          <SelectItem value="__none__">— none —</SelectItem>
                          {shifts.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} ({s.start_time}–{s.end_time})</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Branch</Label>
                      <Select value={attForm.branch_id || "__none__"} onValueChange={(v) => setAttForm({ ...attForm, branch_id: v === "__none__" ? "" : v })}>
                        <SelectTrigger><SelectValue placeholder="— use primary —" /></SelectTrigger>
                        <SelectContent className="bg-white max-h-72">
                          <SelectItem value="__none__">— use primary —</SelectItem>
                          {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name} · {b.type}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label>Check-in</Label><Input type="time" value={attForm.check_in} onChange={(e) => setAttForm({ ...attForm, check_in: e.target.value })} /></div>
                    <div><Label>Check-out</Label><Input type="time" value={attForm.check_out} onChange={(e) => setAttForm({ ...attForm, check_out: e.target.value })} /></div>
                  </div>
                </div>
                <DialogFooter><Button variant="outline" onClick={() => setAttOpen(false)}>Cancel</Button><Button onClick={submitAtt} data-testid="hr-att-save-btn" className="bg-[#C05621] hover:bg-[#9C4221] text-white">Save</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left uc-label border-b border-[#EAE5D9]"><th className="px-4 py-3">Date</th><th className="px-4 py-3">Employee</th><th className="px-4 py-3">Shift</th><th className="px-4 py-3">Branch</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Check-in</th><th className="px-4 py-3">Check-out</th></tr></thead>
              <tbody>
                {attendance.map((a) => (
                  <tr key={a.id} className="border-b border-[#EAE5D9] last:border-0">
                    <td className="px-4 py-3 text-[#8B7355]">{fmtDate(a.date)}</td>
                    <td className="px-4 py-3 font-medium">{a.employee_name}</td>
                    <td className="px-4 py-3">{a.shift_name ? <Badge variant="outline" className="capitalize bg-blue-50 text-blue-700 border-blue-200">{a.shift_name}</Badge> : <span className="text-[#8B7355]">—</span>}</td>
                    <td className="px-4 py-3 text-xs">{a.branch_id ? branchName(a.branch_id) : <span className="text-[#8B7355]">—</span>}</td>
                    <td className="px-4 py-3"><Badge variant="outline" className={`capitalize ${statusColor(a.status)}`}>{a.status?.replace("_", " ")}</Badge></td>
                    <td className="px-4 py-3">{a.check_in || "—"}</td>
                    <td className="px-4 py-3">{a.check_out || "—"}</td>
                  </tr>
                ))}
                {attendance.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-[#8B7355]">No attendance recorded yet</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Leaves */}
        <TabsContent value="leaves" className="pt-4">
          <div className="flex justify-end mb-3">
            <Dialog open={leaveOpen} onOpenChange={setLeaveOpen}>
              <DialogTrigger asChild><Button data-testid="hr-leave-add-btn" className="bg-[#C05621] hover:bg-[#9C4221] text-white"><Plus className="w-4 h-4 mr-1" /> New Leave</Button></DialogTrigger>
              <DialogContent className="max-w-md bg-white">
                <DialogHeader><DialogTitle className="font-[Outfit]">Request Leave</DialogTitle></DialogHeader>
                <div className="space-y-3 py-2">
                  <div>
                    <Label>Employee *</Label>
                    <Select value={leaveForm.employee_id} onValueChange={(v) => setLeaveForm({ ...leaveForm, employee_id: v })}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent className="bg-white">{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Type</Label>
                      <Select value={leaveForm.leave_type} onValueChange={(v) => setLeaveForm({ ...leaveForm, leave_type: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white">{LEAVE_TYPES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div />
                    <div><Label>From *</Label><Input type="date" value={leaveForm.from_date} onChange={(e) => setLeaveForm({ ...leaveForm, from_date: e.target.value })} /></div>
                    <div><Label>To *</Label><Input type="date" value={leaveForm.to_date} onChange={(e) => setLeaveForm({ ...leaveForm, to_date: e.target.value })} /></div>
                  </div>
                  <div><Label>Reason</Label><Textarea rows={2} value={leaveForm.reason} onChange={(e) => setLeaveForm({ ...leaveForm, reason: e.target.value })} /></div>
                </div>
                <DialogFooter><Button variant="outline" onClick={() => setLeaveOpen(false)}>Cancel</Button><Button onClick={submitLeave} data-testid="hr-leave-save-btn" className="bg-[#C05621] hover:bg-[#9C4221] text-white">Request</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left uc-label border-b border-[#EAE5D9]"><th className="px-4 py-3">Employee</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">From</th><th className="px-4 py-3">To</th><th className="px-4 py-3 text-right">Days</th><th className="px-4 py-3">Reason</th><th className="px-4 py-3">Status</th><th></th></tr></thead>
              <tbody>
                {leaves.map((l) => (
                  <tr key={l.id} className="border-b border-[#EAE5D9] last:border-0" data-testid={`hr-leave-row-${l.id}`}>
                    <td className="px-4 py-3 font-medium">{l.employee_name}</td>
                    <td className="px-4 py-3 capitalize">{l.leave_type}</td>
                    <td className="px-4 py-3">{fmtDate(l.from_date)}</td>
                    <td className="px-4 py-3">{fmtDate(l.to_date)}</td>
                    <td className="px-4 py-3 text-right">{l.days}</td>
                    <td className="px-4 py-3 text-[#5C4033] max-w-xs truncate">{l.reason || "—"}</td>
                    <td className="px-4 py-3"><Badge variant="outline" className={`capitalize ${statusColor(l.status)}`}>{l.status}</Badge></td>
                    <td className="px-4 py-3">
                      {l.status === "pending" && (
                        <div className="flex gap-1">
                          <button onClick={() => decideLeave(l, "approved")} data-testid={`hr-leave-approve-${l.id}`} className="p-1 hover:bg-emerald-50 text-emerald-700 rounded"><CheckCircle2 className="w-4 h-4" /></button>
                          <button onClick={() => decideLeave(l, "rejected")} className="p-1 hover:bg-red-50 text-red-600 rounded"><XCircle className="w-4 h-4" /></button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {leaves.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-[#8B7355]">No leaves yet</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Salary */}
        <TabsContent value="salary" className="pt-4">
          <div className="flex justify-end gap-2 mb-3">
            <BulkPayrollButton branches={branches} onDone={load} />
            <Dialog open={salOpen} onOpenChange={(o) => { setSalOpen(o); if (!o) { setSalForm(emptySal); setEditingSal(null); setSalPreview(null); } }}>
              <DialogTrigger asChild><Button data-testid="hr-sal-add-btn" className="bg-[#C05621] hover:bg-[#9C4221] text-white"><Plus className="w-4 h-4 mr-1" /> Record Salary</Button></DialogTrigger>
              <DialogContent className="max-w-lg bg-white">
                <DialogHeader><DialogTitle className="font-[Outfit]">{editingSal ? "Edit Salary" : "Record Salary"}</DialogTitle></DialogHeader>
                <div className="space-y-3 py-2">
                  <div>
                    <Label>Employee *</Label>
                    <Select value={salForm.employee_id} onValueChange={(v) => { const emp = employees.find((x) => x.id === v); setSalForm({ ...salForm, employee_id: v, base_salary: emp?.salary || 0 }); }} disabled={!!editingSal}>
                      <SelectTrigger data-testid="hr-sal-emp-select"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent className="bg-white">{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} · {inr(e.salary)}/mo</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Month *</Label><Input type="month" value={salForm.month} onChange={(e) => setSalForm({ ...salForm, month: e.target.value })} disabled={!!editingSal} /></div>

                  {/* Auto-flow preview strip (only when creating) */}
                  {!editingSal && salPreview && (
                    <div className="rounded-md border border-blue-200 bg-blue-50 p-3 space-y-2" data-testid="hr-sal-preview">
                      <div className="text-xs uppercase text-blue-800 font-semibold">Auto-flow for this month</div>
                      <div className="flex flex-wrap gap-2 items-center text-sm">
                        <Badge className="bg-emerald-100 text-emerald-800 border-0">OT: {salPreview.ot.hours}h · ₹{inr(salPreview.ot.amount)}</Badge>
                        <Badge className="bg-red-100 text-red-800 border-0">Active loans: {salPreview.loans.count} · EMI ₹{inr(salPreview.loans.emi_total)}</Badge>
                        {salPreview.already_deducted_this_month > 0 && <Badge className="bg-amber-100 text-amber-800 border-0">{salPreview.already_deducted_this_month} loan(s) already deducted this month</Badge>}
                      </div>
                      <div className="flex flex-wrap gap-4">
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <input type="checkbox" data-testid="hr-sal-auto-ot" checked={salForm.auto_include_ot} onChange={(e) => setSalForm({ ...salForm, auto_include_ot: e.target.checked })} className="w-4 h-4" />
                          <span>Auto-include OT (₹{inr(salPreview.ot.amount)}) as bonus</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <input type="checkbox" data-testid="hr-sal-auto-loan" checked={salForm.auto_deduct_loans} onChange={(e) => setSalForm({ ...salForm, auto_deduct_loans: e.target.checked })} className="w-4 h-4" />
                          <span>Auto-deduct 1 EMI per active loan (₹{inr(salPreview.loans.emi_total)})</span>
                        </label>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-3 gap-3">
                    <div><Label>Base</Label><Input type="number" value={salForm.base_salary} onChange={(e) => setSalForm({ ...salForm, base_salary: e.target.value })} data-testid="hr-sal-base" /></div>
                    <div><Label>{editingSal ? "Bonus (total)" : "Manual Bonus"}</Label><Input type="number" value={salForm.bonuses} onChange={(e) => setSalForm({ ...salForm, bonuses: e.target.value })} data-testid="hr-sal-bonus" /></div>
                    <div><Label>{editingSal ? "Deduction (total)" : "Manual Deduction"}</Label><Input type="number" value={salForm.deductions} onChange={(e) => setSalForm({ ...salForm, deductions: e.target.value })} data-testid="hr-sal-deduction" /></div>
                  </div>
                  {!editingSal && salPreview && (
                    <div className="text-xs text-[#8B7355]">Final bonus = manual + {salForm.auto_include_ot ? `OT ₹${inr(salPreview.ot.amount)}` : "0"} · Final deduction = manual + {salForm.auto_deduct_loans ? `EMI ₹${inr(salPreview.loans.emi_total)}` : "0"}</div>
                  )}
                  <div><Label>Notes</Label><Textarea rows={2} value={salForm.notes} onChange={(e) => setSalForm({ ...salForm, notes: e.target.value })} /></div>
                  <div className="p-3 rounded bg-[#F7F2E8] flex justify-between">
                    <span className="uc-label">Net Payable (preview)</span>
                    <span className="font-semibold text-[#C05621]">
                      {inr(
                        Number(salForm.base_salary || 0)
                        + Number(salForm.bonuses || 0)
                        + (!editingSal && salPreview && salForm.auto_include_ot ? Number(salPreview.ot.amount) : 0)
                        - Number(salForm.deductions || 0)
                        - (!editingSal && salPreview && salForm.auto_deduct_loans ? Number(salPreview.loans.emi_total) : 0)
                      )}
                    </span>
                  </div>
                </div>
                <DialogFooter><Button variant="outline" onClick={() => setSalOpen(false)}>Cancel</Button><Button onClick={submitSal} data-testid="hr-sal-save-btn" className="bg-[#C05621] hover:bg-[#9C4221] text-white">{editingSal ? "Update" : "Save"}</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left uc-label border-b border-[#EAE5D9]"><th className="px-4 py-3">Employee</th><th className="px-4 py-3">Month</th><th className="px-4 py-3 text-right">Base</th><th className="px-4 py-3 text-right">Bonus (OT)</th><th className="px-4 py-3 text-right">Deduction (Loan)</th><th className="px-4 py-3 text-right">Net</th><th className="px-4 py-3">Status</th><th></th></tr></thead>
              <tbody>
                {salaries.map((s) => (
                  <tr key={s.id} className="border-b border-[#EAE5D9] last:border-0" data-testid={`hr-sal-row-${s.id}`}>
                    <td className="px-4 py-3 font-medium">{s.employee_name}</td>
                    <td className="px-4 py-3">{s.month}</td>
                    <td className="px-4 py-3 text-right">{inr(s.base_salary)}</td>
                    <td className="px-4 py-3 text-right text-emerald-700">
                      {inr(s.bonuses)}
                      {Number(s.ot_bonus || 0) > 0 && <div className="text-[10px] text-emerald-600">incl OT ₹{inr(s.ot_bonus)}</div>}
                    </td>
                    <td className="px-4 py-3 text-right text-red-700">
                      {inr(s.deductions)}
                      {Number(s.loan_deductions_amount || 0) > 0 && <div className="text-[10px] text-red-600">incl EMI ₹{inr(s.loan_deductions_amount)}</div>}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-[#C05621]">{inr(s.net_amount)}</td>
                    <td className="px-4 py-3"><Badge variant="outline" className={s.status === "paid" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}>{s.status}</Badge></td>
                    <td className="px-4 py-3 flex items-center gap-2">
                      {s.status !== "paid" && (
                        <>
                          <Button size="sm" variant="outline" data-testid={`hr-sal-edit-${s.id}`} onClick={() => { setSalForm({ ...emptySal, ...s }); setEditingSal(s.id); setSalOpen(true); }}>Edit</Button>
                          <Button size="sm" onClick={() => markPaid(s)} data-testid={`hr-sal-pay-${s.id}`} className="bg-emerald-600 hover:bg-emerald-700 text-white h-7"><IndianRupee className="w-3 h-3 mr-1" /> Mark Paid</Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
                {salaries.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-[#8B7355]">No salaries yet</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}


function AssignBranchButton({ employees, branches, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ employee_id: "", branch_id: "", reason: "" });
  const emp = employees.find((e) => e.id === f.employee_id);
  const currentBranch = emp?.primary_branch_id ? branches.find((b) => b.id === emp.primary_branch_id) : null;
  const save = async () => {
    if (!f.employee_id || !f.branch_id) return toast.error("Employee & branch required");
    try {
      const r = await api.post("/hr/assign-branch", f);
      toast.success(`${emp?.name || "Staff"} → ${r.data.branch_name}${r.data.transferred ? " (transferred)" : ""}`);
      setOpen(false); setF({ employee_id: "", branch_id: "", reason: "" });
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setF({ employee_id: "", branch_id: "", reason: "" }); }}>
      <DialogTrigger asChild>
        <Button data-testid="hr-assign-branch-btn" variant="outline" className="border-[#C05621] text-[#C05621] hover:bg-[#FEEBC8]">
          <ArrowRightLeft className="w-4 h-4 mr-1" /> Assign to Branch
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md bg-white">
        <DialogHeader><DialogTitle className="font-[Outfit]">Assign Staff to Branch</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Staff *</Label>
            <Select value={f.employee_id} onValueChange={(v) => setF({ ...f, employee_id: v })}>
              <SelectTrigger data-testid="hr-assign-emp"><SelectValue placeholder="Pick a staff member" /></SelectTrigger>
              <SelectContent className="bg-white max-h-72">{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} · {e.employee_code}</SelectItem>)}</SelectContent>
            </Select>
            {emp && (
              <div className="mt-1.5 text-xs text-[#8B7355]">
                Currently at: <b className="text-[#2C1810]">{currentBranch ? `${currentBranch.name} · ${currentBranch.type}` : "unassigned"}</b>
              </div>
            )}
          </div>
          <div>
            <Label>Assign to Branch *</Label>
            <Select value={f.branch_id} onValueChange={(v) => setF({ ...f, branch_id: v })}>
              <SelectTrigger data-testid="hr-assign-branch"><SelectValue placeholder="Factory / Showroom / HO" /></SelectTrigger>
              <SelectContent className="bg-white max-h-72">{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name} · {b.type}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Reason (optional)</Label>
            <Input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} placeholder="e.g. Nadavaramb showroom launch" data-testid="hr-assign-reason" />
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded p-2 text-xs text-blue-800">
            The staff member will immediately appear on the destination branch's dashboard for daily Attendance, OT and Leave tracking. Historical records stay with the employee.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={save} data-testid="hr-assign-save" className="bg-[#C05621] text-white">Assign</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkPayrollButton({ branches, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({
    month: new Date().toISOString().slice(0, 7),
    branch_id: "",
    department: "",
    auto_include_ot: true,
    auto_deduct_loans: true,
    only_missing: true,
    include_zero_salary: false,
  });
  const [preview, setPreview] = useState(null);
  const [running, setRunning] = useState(false);

  const doPreview = async () => {
    setRunning(true);
    try {
      const r = await api.post("/hr/salary/bulk-run", {
        ...f,
        branch_id: f.branch_id || null,
        department: f.department || null,
        preview: true,
      });
      setPreview(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
    finally { setRunning(false); }
  };
  const runIt = async () => {
    if (!window.confirm(`Create ${preview?.preview_rows?.length || 0} unpaid salary records for ${f.month}?`)) return;
    setRunning(true);
    try {
      const r = await api.post("/hr/salary/bulk-run", {
        ...f,
        branch_id: f.branch_id || null,
        department: f.department || null,
        preview: false,
      });
      toast.success(`Payroll run: ${r.data.created_count} created · ${r.data.skipped_count} skipped · ₹${r.data.total_net_amount.toLocaleString("en-IN")} total`);
      setOpen(false);
      setPreview(null);
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Run failed"); }
    finally { setRunning(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setPreview(null); }}>
      <DialogTrigger asChild>
        <Button data-testid="hr-bulk-payroll-btn" variant="outline" className="border-emerald-600 text-emerald-700 hover:bg-emerald-50">
          <IndianRupee className="w-4 h-4 mr-1" /> Run Payroll for All
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl bg-white">
        <DialogHeader>
          <DialogTitle className="font-[Outfit] text-2xl">Bulk Payroll Run</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Month *</Label><Input type="month" value={f.month} onChange={(e) => { setF({ ...f, month: e.target.value }); setPreview(null); }} data-testid="hr-bulk-month" /></div>
            <div>
              <Label>Branch (optional)</Label>
              <Select value={f.branch_id || "__all__"} onValueChange={(v) => { setF({ ...f, branch_id: v === "__all__" ? "" : v }); setPreview(null); }}>
                <SelectTrigger data-testid="hr-bulk-branch"><SelectValue placeholder="All branches" /></SelectTrigger>
                <SelectContent className="bg-white max-h-72">
                  <SelectItem value="__all__">All branches</SelectItem>
                  {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name} · {b.type}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Department (optional)</Label>
              <Select value={f.department || "__all__"} onValueChange={(v) => { setF({ ...f, department: v === "__all__" ? "" : v }); setPreview(null); }}>
                <SelectTrigger data-testid="hr-bulk-dept"><SelectValue placeholder="All departments" /></SelectTrigger>
                <SelectContent className="bg-white max-h-72">
                  <SelectItem value="__all__">All departments</SelectItem>
                  {DEPARTMENTS.map((d) => <SelectItem key={d} value={d} className="capitalize">{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 text-sm bg-[#FAF6EE] border border-[#EAE5D9] rounded p-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" className="w-4 h-4" checked={f.auto_include_ot} onChange={(e) => setF({ ...f, auto_include_ot: e.target.checked })} data-testid="hr-bulk-auto-ot" />
              <span>Auto-include OT from this month</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" className="w-4 h-4" checked={f.auto_deduct_loans} onChange={(e) => setF({ ...f, auto_deduct_loans: e.target.checked })} data-testid="hr-bulk-auto-loan" />
              <span>Auto-deduct active loan EMIs</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" className="w-4 h-4" checked={f.only_missing} onChange={(e) => setF({ ...f, only_missing: e.target.checked })} />
              <span>Skip employees who already have {f.month} salary</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" className="w-4 h-4" checked={f.include_zero_salary} onChange={(e) => setF({ ...f, include_zero_salary: e.target.checked })} />
              <span>Include employees with ₹0 base salary</span>
            </label>
          </div>

          <div className="flex items-center gap-2">
            <Button onClick={doPreview} disabled={running} variant="outline" data-testid="hr-bulk-preview">
              {running ? "Loading…" : "Preview Impact"}
            </Button>
            {preview && (
              <div className="text-sm">
                <Badge className="bg-emerald-100 text-emerald-800 border-0">Will create: {preview.preview_rows.length}</Badge>{" "}
                <Badge className="bg-stone-100 text-stone-700 border-0">Will skip: {preview.skipped_count}</Badge>{" "}
                <Badge className="bg-purple-100 text-purple-800 border-0">Net ₹{Number(preview.total_net_amount).toLocaleString("en-IN")}</Badge>
              </div>
            )}
          </div>

          {preview && preview.preview_rows.length > 0 && (
            <div className="max-h-64 overflow-y-auto border border-[#EAE5D9] rounded-md" data-testid="hr-bulk-preview-list">
              <table className="w-full text-xs">
                <thead className="bg-[#FAF6EE] text-[#8B7355] uppercase sticky top-0">
                  <tr><th className="text-left px-3 py-2">Employee</th><th className="text-right px-3 py-2">Base</th><th className="text-right px-3 py-2">OT</th><th className="text-right px-3 py-2">Loan</th><th className="text-right px-3 py-2">Net</th></tr>
                </thead>
                <tbody>
                  {preview.preview_rows.map((r) => (
                    <tr key={r.employee_id} className="border-t border-[#EAE5D9]">
                      <td className="px-3 py-1.5">{r.employee_name}</td>
                      <td className="px-3 py-1.5 text-right">{inr(r.base_salary)}</td>
                      <td className="px-3 py-1.5 text-right text-emerald-700">{r.ot_bonus > 0 ? `+${inr(r.ot_bonus)}` : "—"}</td>
                      <td className="px-3 py-1.5 text-right text-red-700">{r.loan_deductions > 0 ? `-${inr(r.loan_deductions)}` : "—"}</td>
                      <td className="px-3 py-1.5 text-right font-semibold">{inr(r.net_amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {preview && preview.skipped.length > 0 && (
            <details className="text-xs">
              <summary className="cursor-pointer text-[#8B7355]">Skipped ({preview.skipped.length}) — click to see</summary>
              <div className="max-h-40 overflow-y-auto mt-1 pl-3 space-y-0.5" data-testid="hr-bulk-skipped">
                {preview.skipped.map((s, i) => <div key={i}>· {s.employee_name} <span className="text-[#8B7355]">— {s.reason.replace(/_/g, " ")}</span></div>)}
              </div>
            </details>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={runIt} data-testid="hr-bulk-run" disabled={running || !preview || preview.preview_rows.length === 0} className="bg-emerald-600 hover:bg-emerald-700 text-white">
            {running ? "Running…" : preview ? `Create ${preview.preview_rows.length} salaries` : "Preview first"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

